import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { UsersIcon, FilterIcon, SearchIcon, LayoutGridIcon, LayoutListIcon, PencilIcon, WrenchIcon, HeartHandshakeIcon, CogIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { ProfileCard } from '../../components/Directory/ProfileCard';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../../components/UI/FilterSidebar';
import { SearchWithAutocomplete } from '../../components/UI/SearchWithAutocomplete';
import { mapServiceToProfileCard } from '../../utils/profileCardMappers';
// Mock Data
const serviceProviders = [{
  id: 1,
  name: "Emma's Editing Services",
  category: 'editing',
  subcategory: 'Developmental Editing',
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.9,
  reviewCount: 127,
  location: 'New York, NY',
  remote: true,
  bio: 'Specializing in developmental editing for romance and fantasy novels with 10+ years of experience.',
  tags: ['Romance', 'Fantasy', 'YA'],
  featured: true,
  availability: 'Available',
  startingRate: 0.08,
  rateUnit: 'per word',
  contentBoundaries: ['No erotica', 'No extreme violence', 'No horror'],
  debutFriendly: true
}, {
  id: 2,
  name: 'Clear Copy Editing',
  category: 'editing',
  subcategory: 'Copy Editing',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.7,
  reviewCount: 89,
  location: 'Chicago, IL',
  remote: true,
  bio: 'Meticulous copy editor with a passion for perfecting grammar, syntax, and style across all genres.',
  tags: ['Non-Fiction', 'Mystery', 'Academic'],
  featured: false,
  availability: 'Limited',
  startingRate: 0.04,
  rateUnit: 'per word',
  contentBoundaries: ['No political content', 'No religious debates'],
  debutFriendly: true
}, {
  id: 3,
  name: 'ProofPerfect',
  category: 'editing',
  subcategory: 'Proofreading',
  image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.8,
  reviewCount: 103,
  location: 'Seattle, WA',
  remote: true,
  bio: 'Final-pass proofreading to catch those last-minute errors before publication.',
  tags: ['Fiction', 'Non-Fiction', 'Academic'],
  featured: false,
  availability: 'Booked',
  startingRate: 0.02,
  rateUnit: 'per word',
  contentBoundaries: ['No explicit sexual content'],
  debutFriendly: false
}, {
  id: 4,
  name: 'Pristine Productions',
  category: 'production',
  subcategory: 'Formatting',
  image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.6,
  reviewCount: 78,
  location: 'Los Angeles, CA',
  remote: true,
  bio: 'Expert ebook and print formatting to make your book look professional inside and out.',
  tags: ['Ebook', 'Print', 'Fixed Layout'],
  featured: true,
  availability: 'Available',
  startingRate: 150,
  rateUnit: 'flat fee',
  contentBoundaries: ['No adult content'],
  debutFriendly: true
}, {
  id: 5,
  name: 'Audiobook Artisans',
  category: 'production',
  subcategory: 'Audio Production',
  image: 'https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.9,
  reviewCount: 64,
  location: 'Austin, TX',
  remote: true,
  bio: 'Full-service audiobook production from casting to final mastering.',
  tags: ['Fiction', 'Non-Fiction', 'Multi-cast'],
  featured: false,
  availability: 'Limited',
  startingRate: 300,
  rateUnit: 'per finished hour',
  contentBoundaries: ['No extreme violence', 'No explicit content'],
  debutFriendly: false
}, {
  id: 6,
  name: 'Marketing Mavericks',
  category: 'support',
  subcategory: 'Marketing',
  image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.8,
  reviewCount: 92,
  location: 'Miami, FL',
  remote: true,
  bio: 'Strategic book marketing to help your title stand out in a crowded marketplace.',
  tags: ['Social Media', 'Email', 'Advertising'],
  featured: true,
  availability: 'Available',
  startingRate: 500,
  rateUnit: 'package',
  contentBoundaries: ['No adult content', 'No political campaigns'],
  debutFriendly: true
}, {
  id: 7,
  name: "Author's Assistant",
  category: 'support',
  subcategory: 'Virtual Assistant',
  image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.7,
  reviewCount: 58,
  location: 'Denver, CO',
  remote: true,
  bio: "Virtual assistant services specifically tailored to authors' needs.",
  tags: ['Admin', 'Social Media', 'Research'],
  featured: false,
  availability: 'Limited',
  startingRate: 25,
  rateUnit: 'per hour',
  contentBoundaries: ['No explicit material research'],
  debutFriendly: true
}, {
  id: 8,
  name: 'Beta Readers United',
  category: 'support',
  subcategory: 'Beta Reading',
  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.5,
  reviewCount: 73,
  location: 'Portland, OR',
  remote: true,
  bio: 'Organized beta reading services with detailed feedback reports.',
  tags: ['Romance', 'Fantasy', 'Mystery'],
  featured: false,
  availability: 'Available',
  startingRate: 0.01,
  rateUnit: 'per word',
  contentBoundaries: ['No horror', 'No extreme violence'],
  debutFriendly: true
}, {
  id: 9,
  name: 'Manuscript Mentors',
  category: 'editing',
  subcategory: 'Mentoring',
  image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.9,
  reviewCount: 42,
  location: 'Boston, MA',
  remote: true,
  bio: 'Personalized mentoring to help authors develop their craft and manuscripts.',
  tags: ['Fiction', 'Non-Fiction', 'Career'],
  featured: false,
  availability: 'Booked',
  startingRate: 75,
  rateUnit: 'per hour',
  contentBoundaries: ['No explicit content', 'No hate speech'],
  debutFriendly: true
}, {
  id: 10,
  name: 'Print Perfect',
  category: 'production',
  subcategory: 'Printing',
  image: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.6,
  reviewCount: 87,
  location: 'San Francisco, CA',
  remote: false,
  bio: 'Quality print services for independent authors, including special editions.',
  tags: ['Hardcover', 'Paperback', 'Special Editions'],
  featured: true,
  availability: 'Available',
  startingRate: 200,
  rateUnit: 'package',
  contentBoundaries: ['No adult content', 'No graphic violence'],
  debutFriendly: true
}, {
  id: 11,
  name: "Author's Web",
  category: 'support',
  subcategory: 'Web Design',
  image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.8,
  reviewCount: 65,
  location: 'Atlanta, GA',
  remote: true,
  bio: 'Custom author websites that convert visitors to readers and fans.',
  tags: ['Web Design', 'SEO', 'Maintenance'],
  featured: false,
  availability: 'Limited',
  startingRate: 750,
  rateUnit: 'project',
  contentBoundaries: ['No adult content sites', 'No gambling content'],
  debutFriendly: true
}, {
  id: 12,
  name: 'Publicity Partners',
  category: 'support',
  subcategory: 'Publicity',
  image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&h=256&q=80',
  rating: 4.7,
  reviewCount: 54,
  location: 'Nashville, TN',
  remote: true,
  bio: 'Book publicity services to get your title in front of readers, reviewers, and media.',
  tags: ['Media Relations', 'Book Tours', 'Press'],
  featured: true,
  availability: 'Available',
  startingRate: 400,
  rateUnit: 'package',
  contentBoundaries: ['No explicit content', 'No political campaigns'],
  debutFriendly: true
}];
const ServicesHub = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // URL Params
  const typeParam = searchParams.get('type') || 'all';
  const queryParam = searchParams.get('q') || '';
  // State
  const [activeTab, setActiveTab] = useState(typeParam);
  const [searchQuery, setSearchQuery] = useState(queryParam);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Filter States
  const [sortBy, setSortBy] = useState('relevance');
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState('any');
  const [selectedSpecial, setSelectedSpecial] = useState<string[]>([]);
  // Sync state with URL
  useEffect(() => {
    if (typeParam && typeParam !== activeTab) {
      setActiveTab(typeParam);
    }
    if (queryParam && queryParam !== searchQuery) {
      setSearchQuery(queryParam);
    }
  }, [typeParam, queryParam]);
  const updateUrl = (type: string, query: string) => {
    const params: any = {};
    if (type && type !== 'all') params.type = type;
    if (query) params.q = query;
    setSearchParams(params);
  };
  const handleTabChange = (type: string) => {
    setActiveTab(type);
    updateUrl(type, searchQuery);
    // Clear category filters when switching tabs to avoid confusion
    setSelectedCategories([]);
  };
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    updateUrl(activeTab, query);
  };
  // Filter Logic
  const filteredProviders = useMemo(() => {
    let result = [...serviceProviders];
    // 1. Filter by Tab (Category)
    if (activeTab !== 'all') {
      result = result.filter(p => p.category === activeTab);
    }
    // 2. Filter by Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q) || p.subcategory.toLowerCase().includes(q) || p.bio.toLowerCase().includes(q) || p.tags.some(t => t.toLowerCase().includes(q)));
    }
    // 3. Filter by Availability
    if (selectedAvailability.length > 0) {
      result = result.filter(p => selectedAvailability.includes(p.availability));
    }
    // 4. Filter by Category (Sidebar)
    if (selectedCategories.length > 0) {
      result = result.filter(p => selectedCategories.includes(p.category));
    }
    // 5. Filter by Service Type (Subcategory)
    if (selectedServiceTypes.length > 0) {
      result = result.filter(p => selectedServiceTypes.includes(p.subcategory));
    }
    // 6. Filter by Price Range
    if (selectedPriceRange !== 'any') {
      // Simplified price logic for mock data
      if (selectedPriceRange === 'economy') {
        result = result.filter(p => p.rateUnit === 'per word' && p.startingRate < 0.03 || p.rateUnit === 'per hour' && p.startingRate < 50 || p.rateUnit === 'flat fee' && p.startingRate < 200);
      } else if (selectedPriceRange === 'standard') {
        result = result.filter(p => p.rateUnit === 'per word' && p.startingRate >= 0.03 && p.startingRate <= 0.06 || p.rateUnit === 'per hour' && p.startingRate >= 50 && p.startingRate <= 100 || p.rateUnit === 'flat fee' && p.startingRate >= 200 && p.startingRate <= 500);
      } else if (selectedPriceRange === 'premium') {
        result = result.filter(p => p.rateUnit === 'per word' && p.startingRate > 0.06 || p.rateUnit === 'per hour' && p.startingRate > 100 || p.rateUnit === 'flat fee' && p.startingRate > 500);
      }
    }
    // 7. Filter by Special Features
    if (selectedSpecial.length > 0) {
      if (selectedSpecial.includes('debut')) {
        result = result.filter(p => p.debutFriendly);
      }
      if (selectedSpecial.includes('remote')) {
        result = result.filter(p => p.remote);
      }
    }
    // 8. Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'rating-desc':
          return b.rating - a.rating;
        case 'reviews-desc':
          return b.reviewCount - a.reviewCount;
        default:
          // relevance
          return 0;
      }
    });
    return result;
  }, [activeTab, searchQuery, sortBy, selectedAvailability, selectedCategories, selectedServiceTypes, selectedPriceRange, selectedSpecial]);
  // Filter Configuration
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'relevance',
      label: 'Relevance'
    }, {
      value: 'name-asc',
      label: 'Name (A-Z)'
    }, {
      value: 'rating-desc',
      label: 'Highest Rated'
    }, {
      value: 'reviews-desc',
      label: 'Most Reviews'
    }],
    value: sortBy,
    onChange: setSortBy
  }, {
    id: 'availability',
    title: 'Availability',
    type: 'checkbox',
    options: [{
      value: 'Available',
      label: 'Available Now'
    }, {
      value: 'Limited',
      label: 'Limited Availability'
    }, {
      value: 'Booked',
      label: 'Booked'
    }],
    value: selectedAvailability,
    onChange: val => setSelectedAvailability(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  },
  // Only show Category filter if "All" tab is selected
  ...(activeTab === 'all' ? [{
    id: 'category',
    title: 'Category',
    type: 'checkbox',
    options: [{
      value: 'editing',
      label: 'Editing'
    }, {
      value: 'production',
      label: 'Production'
    }, {
      value: 'support',
      label: 'Support'
    }],
    value: selectedCategories,
    onChange: (val: string) => setSelectedCategories(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }] : []), {
    id: 'serviceType',
    title: 'Service Type',
    type: 'checkbox',
    options: [{
      value: 'Developmental Editing',
      label: 'Developmental Editing'
    }, {
      value: 'Copy Editing',
      label: 'Copy Editing'
    }, {
      value: 'Proofreading',
      label: 'Proofreading'
    }, {
      value: 'Formatting',
      label: 'Formatting'
    }, {
      value: 'Cover Design',
      label: 'Cover Design'
    }, {
      value: 'Audio Production',
      label: 'Audio Production'
    }, {
      value: 'Marketing',
      label: 'Marketing'
    }, {
      value: 'Virtual Assistant',
      label: 'Virtual Assistant'
    }],
    value: selectedServiceTypes,
    onChange: val => setSelectedServiceTypes(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'price',
    title: 'Price Range',
    type: 'radio',
    options: [{
      value: 'any',
      label: 'Any Price'
    }, {
      value: 'economy',
      label: 'Economy'
    }, {
      value: 'standard',
      label: 'Standard'
    }, {
      value: 'premium',
      label: 'Premium'
    }],
    value: selectedPriceRange,
    onChange: setSelectedPriceRange
  }, {
    id: 'special',
    title: 'Special Features',
    type: 'checkbox',
    options: [{
      value: 'debut',
      label: 'Debut Friendly'
    }, {
      value: 'remote',
      label: 'Remote Only'
    }],
    value: selectedSpecial,
    onChange: val => setSelectedSpecial(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }];
  const handleResetFilters = () => {
    setSortBy('relevance');
    setSelectedAvailability([]);
    setSelectedCategories([]);
    setSelectedServiceTypes([]);
    setSelectedPriceRange('any');
    setSelectedSpecial([]);
    setSearchQuery('');
  };
  const tabs = [{
    id: 'all',
    label: 'All',
    icon: UsersIcon
  }, {
    id: 'editing',
    label: 'Editing',
    icon: PencilIcon
  }, {
    id: 'production',
    label: 'Production',
    icon: WrenchIcon
  }, {
    id: 'support',
    label: 'Support',
    icon: HeartHandshakeIcon
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-13655" data-mp="112hip2eb1vu3">
      {/* Header Section */}
      <div className={`pt-8 pb-6 px-4 sm:px-6 lg:px-8 ${dk.surface} border-b ${dk.border}`} data-id="element-13656" data-mp="3iekqi7abglp">
        <div className="max-w-7xl mx-auto" data-id="element-13657" data-mp="8g68d00wcgk3">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8" data-id="element-13658" data-mp="dehjrf6uaz4f">
            <div data-id="element-13659" data-mp="i67ydxct891n">
              <h1 className={`text-3xl font-bold mb-2 ${dk.textHeading}`} data-id="element-13660" data-mp="i6rhhhe753h9">
                Services Hub
              </h1>
              <p className={`text-lg ${dk.textSecondary}`} data-id="element-13661" data-mp="3gv6qp2ac4aum">
                Discover and connect with professional editors, production
                specialists, and support services.
              </p>
            </div>
            <div className="w-full md:w-96" data-id="element-13662" data-mp="3e97e59rjrq3f">
              <SearchWithAutocomplete value={searchQuery} onChange={handleSearch} placeholder="Search services..." variant="pill" suggestions={[]} data-id="element-13663" data-mp="39bxhb91twc5k" />
            </div>
          </div>

          {/* Filter Pills (Tabs) */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0" data-id="element-13664" data-mp="2vdh9tfw56hmd">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            return <button key={tab.id} onClick={() => handleTabChange(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap border ${isActive ? `${rt.bgDark} text-white border-transparent shadow-md` : `${dk.surface} ${dk.textSecondary} ${dk.border} hover:${dk.surfaceMuted}`}`} data-id="element-13665" data-mp="2gnrvsk2yaj4o">
                  <Icon className="h-4 w-4" data-id="element-13666" data-mp="rk2aidjdrtc1" />
                  {tab.label}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-13667" data-mp="ik5grxi4w4a4">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-13668" data-mp="ilym99h0gigj">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden mb-4" data-id="element-13669" data-mp="dmdr7a4you93">
            <button onClick={() => setSidebarOpen(true)} className={`w-full flex items-center justify-center px-4 py-3 border rounded-lg shadow-sm ${dk.card} ${dk.textPrimary}`} data-id="element-13670" data-mp="8srmc451hunu">
              <FilterIcon className="h-5 w-5 mr-2" data-id="element-13671" data-mp="3w03ek4l76eig" />
              Filters & Sort
            </button>
          </div>

          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-13672" data-mp="3m0chpw95znf7">
            <FilterSidebar title="Filters" sections={filterSections} onReset={handleResetFilters} searchable={false} data-id="element-13673" data-mp="26g3ieqsx60q1" />
          </FilterSidebarWrapper>

          {/* Results Area */}
          <div className="flex-1 min-w-0" data-id="element-13674" data-mp="1irsroggxthfh">
            {/* Results Header & View Toggle */}
            <div className="flex items-center justify-between mb-6" data-id="element-13675" data-mp="1nqwwkuwtwt00">
              <h2 className={`text-lg font-medium ${dk.textPrimary}`} data-id="element-13676" data-mp="1nr5bescii8du">
                {filteredProviders.length} result
                {filteredProviders.length !== 1 && 's'} found
              </h2>

              <div className={`flex rounded-lg p-1 border ${dk.border} ${dk.surface}`} data-id="element-13677" data-mp="1wkicihkqcgb0">
                <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? `${rt.bgDark} text-white shadow-sm` : `${dk.textMuted} hover:${dk.textPrimary}`}`} aria-label="Grid view" data-id="element-13678" data-mp="zzi03irqh5wo">
                  <LayoutGridIcon className="h-4 w-4" data-id="element-13679" data-mp="2h3n63igxcjec" />
                </button>
                <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? `${rt.bgDark} text-white shadow-sm` : `${dk.textMuted} hover:${dk.textPrimary}`}`} aria-label="List view" data-id="element-13680" data-mp="2h5ptduqozyfd">
                  <LayoutListIcon className="h-4 w-4" data-id="element-13681" data-mp="1ji7nqssbvcfl" />
                </button>
              </div>
            </div>

            {/* Grid/List */}
            {filteredProviders.length > 0 ? <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`} data-id="element-13682" data-mp="1tg5blf69z77o">
                {filteredProviders.map(provider => <ProfileCard key={provider.id} data={mapServiceToProfileCard(provider)} viewMode={viewMode} data-id="element-13683" data-mp="3ccqh0x4auk8c" />)}
              </div> : <div className={`text-center py-16 rounded-xl border-2 border-dashed ${dk.border} ${dk.surfaceMuted}`} data-id="element-13684" data-mp="32fugpeok14ev">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-13685" data-mp="3v385uqm0gvof">
                  <SearchIcon className={`h-8 w-8 ${dk.textMuted}`} data-id="element-13686" data-mp="3z02m0oeascd" />
                </div>
                <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-13687" data-mp="3lbc7deg0j3jo">
                  No services found
                </h3>
                <p className={`max-w-md mx-auto ${dk.textSecondary}`} data-id="element-13688" data-mp="hwybiqjy5wkh">
                  Try adjusting your filters or search terms.
                </p>
                <button onClick={handleResetFilters} className={`mt-6 font-medium ${rt.accentLink}`} data-id="element-13689" data-mp="mopsqah7qpxy">
                  Clear all filters
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default ServicesHub;